import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ImageOff, Upload } from 'lucide-react';
import { ApiError, api } from '@/lib/api';
import { Button, Note, Skeleton } from '@/components/ui';
import { IMAGE_ACCEPT, IMAGE_RULE_HINT, imageFileProblem } from '@/lib/images';
import { ImageCropDialog, type CropSpec } from '@/components/ImageCropDialog';

/**
 * صورة خلف رأس Authorization.
 * `<img src>` لا يحمل رؤوساً، فتُقرأ البايتات Blob ويُصنع رابط كائن يُبطَل عند
 * التفريغ — وإلا تسرّبت روابط الكائنات مع كل إعادة تحميل.
 * الحالات الثلاث معالجة صراحةً: انتظار، ولا صورة (404)، ومنعٌ بقاعدة عمل
 * يُبلَّغ للصفحة الحاضنة عبر `onDenied` لتقول السبب بنفسها.
 */
export function ProtectedImage({
  path, alt, className = '', anonymous = false, onDenied, onStatus, empty, size = 120, rounded,
}: {
  /** `null` يعني «لا صورة، ولا تسأل»: فلا يُطلق طلبٌ يُردّ 404 على كل فتحة صفحة */
  path: string | null;
  alt: string;
  className?: string;
  anonymous?: boolean;
  /** يُنادى مرةً عند خطأ قاعدة عمل (PHOTO-001 مثلاً) بالرسالة العربية وكودها */
  onDenied?: (error: ApiError) => void;
  /**
   * حالة الصورة كما هي فعلاً: موجودة، أو غير موجودة (404)، أو محجوبة بقاعدة
   * عمل. الصفحة الحاضنة تحتاجها لتعرف مثلاً أن «احذف الصورة» لا معنى له.
   */
  onStatus?: (status: 'ready' | 'missing' | 'denied') => void;
  /** ما يُعرض حين لا توجد صورة */
  empty?: ReactNode;
  size?: number;
  rounded?: boolean;
}) {
  /**
   * المخزَّن **Blob** لا رابطُ كائن (18 سبتمبر 2026): الرابطُ يملك دورةَ حياةٍ
   * ينهيها هذا المكوّن عند تفريغه، فتخزينُ نصِّه كان يترك في الذاكرة رابطاً
   * ميتاً تُسلَّمه الزيارةُ التالية — فتُكسر الصورة حتى يُعاد تحميل الصفحة.
   * الـBlob لا يُبطَل، وكلُّ نسخةٍ من المكوّن تصنع رابطَها وتُبطله هي.
   */
  const q = useQuery({
    queryKey: ['blob', path, anonymous],
    queryFn: () => api.blob(path as string, anonymous ? { anonymous: true, institutionId: null } : {}),
    enabled: path !== null,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const url = useMemo(() => (q.data ? URL.createObjectURL(q.data) : null), [q.data]);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  const denied = useRef(false);
  useEffect(() => {
    const e = q.error;
    if (!denied.current && e instanceof ApiError && e.isBusinessRule) { denied.current = true; onDenied?.(e); }
  }, [q.error, onDenied]);

  useEffect(() => {
    if (path === null) { onStatus?.('missing'); return; }
    if (q.isLoading) return;
    if (q.data) { onStatus?.('ready'); return; }
    const e = q.error;
    onStatus?.(e instanceof ApiError && e.isBusinessRule ? 'denied' : 'missing');
  }, [path, q.isLoading, q.data, q.error, onStatus]);

  if (q.isLoading) return <Skeleton height={size} width={size} />;
  if (q.isError || !url) {
    return (
      <div className={`media-empty${rounded ? ' media-empty--round' : ''} ${className}`.trim()}
        style={{ inlineSize: size, blockSize: size }}>
        {empty ?? <><ImageOff size={22} aria-hidden /><span className="caption">لا صورة</span></>}
      </div>
    );
  }
  return (
    <img
      src={url} alt={alt}
      className={`media-img${rounded ? ' media-img--round' : ''} ${className}`.trim()}
      style={{ inlineSize: size, blockSize: size }}
    />
  );
}

/**
 * منتقي صورة بمعاينة حيّة: الملف لا يُرسَل حتى يراه المستخدم على الشاشة.
 * الفحص المحلّي يسبق الرفع، والخطأ يُعرَض في مكانه لا في تنبيهٍ عائم.
 */
export function ImagePicker({
  label, hint, onPick, picked, onClear, busy, disabled, disabledReason, previewSize = 120, rounded, crop,
}: {
  label: string;
  hint?: string;
  onPick: (file: File) => void;
  picked: File | null;
  onClear: () => void;
  busy?: boolean;
  disabled?: boolean;
  disabledReason?: string;
  previewSize?: number;
  rounded?: boolean;
  /** بمواصفةٍ: تُفتح نافذةُ القصّ قبل أن يُعدّ الملفُ مختاراً (24 سبتمبر 2026) */
  crop?: CropSpec;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [cropping, setCropping] = useState<File | null>(null);
  // المعاينة مشتقّة من الملف لا حالةٌ ثانية تُزامَن — والرابط يُبطَل عند تغيّره
  const preview = useMemo(() => (picked ? URL.createObjectURL(picked) : null), [picked]);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  return (
    <div className="stack gap-3">
      <input
        ref={input} type="file" accept={IMAGE_ACCEPT} className="sr-only" aria-label={label}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file) return;
          const bad = imageFileProblem(file);
          setProblem(bad);
          if (bad) return;
          if (crop) setCropping(file); else onPick(file);
        }}
      />
      {cropping && crop && (
        <ImageCropDialog file={cropping} spec={crop} onCancel={() => setCropping(null)} onDone={(f) => { setCropping(null); onPick(f); }} />
      )}
      {problem && <Note kind="danger">{problem}</Note>}
      {preview && (
        <div className="row gap-3 wrap">
          <img src={preview} alt="معاينة الصورة قبل الرفع"
            className={`media-img${rounded ? ' media-img--round' : ''}`}
            style={{ inlineSize: previewSize, blockSize: previewSize }} />
          <div className="stack gap-1">
            <span className="small">معاينة قبل الرفع{crop ? ` — تُحفظ ${crop.outputWidth}×${Math.round(crop.outputWidth / crop.aspect)}` : ''}</span>
            <span className="caption ltr">{picked?.name}</span>
            <Button size="sm" variant="ghost" onClick={onClear} disabled={busy}
              disabledReason={busy ? 'جارٍ الرفع الآن.' : undefined}>أزل الاختيار</Button>
          </div>
        </div>
      )}
      <div className="row gap-2 wrap">
        <Button
          variant="secondary" icon={<Upload size={16} />} loading={busy}
          disabled={disabled} disabledReason={disabled ? disabledReason : undefined}
          onClick={() => { setProblem(null); input.current?.click(); }}
        >
          {label}
        </Button>
        <span className="caption">{hint ?? IMAGE_RULE_HINT}</span>
      </div>
    </div>
  );
}
